import { useEffect, useId, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { Link, useRouterState } from '@tanstack/react-router'
import { heroContent } from '../../data/homepage'
import { bookNowHref, primaryNavItems } from '../../lib/public-routes'
import { ButtonLink } from '../ui/button'

export interface SiteHeaderProps {}

export function SiteHeader(_: Readonly<SiteHeaderProps>) {
  const [open, setOpen] = useState(false)
  const menuId = useId()
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  function isActive(href: string) {
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(`${href}/`)
  }

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  return (
    <header className="site-header">
      <a className="skip-link" href="#main">Skip to main content</a>
      <div className="site-header-inner">
        <Link className="brand-link" to="/" aria-label="Autoglow Detailing home">
          <img src={heroContent.logo} alt="Autoglow Detailing Logo" width="2481" height="785" />
        </Link>

        <nav className="desktop-nav" aria-label="Primary navigation">
          {primaryNavItems.map((item) => (
            <Link className={isActive(item.href) ? 'active' : ''} to={item.href} key={item.label}>
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="header-actions">
          <ButtonLink className={pathname === bookNowHref ? 'book-now active' : 'book-now'} href={bookNowHref}>
            Book Now
          </ButtonLink>
          <button
            className="menu-trigger"
            type="button"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <nav className={open ? 'mobile-menu is-open' : 'mobile-menu'} id={menuId} aria-label="Mobile navigation">
        {primaryNavItems.map((item) => (
          <Link
            className={isActive(item.href) ? 'active' : ''}
            to={item.href}
            key={item.label}
            onClick={() => setOpen(false)}
          >
            {item.label}
          </Link>
        ))}
        <ButtonLink href={bookNowHref} onClick={() => setOpen(false)}>
          Book Now
        </ButtonLink>
      </nav>
    </header>
  )
}
